<script lang="ts">
  // Was zeigt diese Karte? Darstellung, Leitwert (die grosse Zahl) und
  // die Felder darunter. Bewusst ein kleiner Dialog am <body> statt
  // eines Popovers in der Karte: die Karte liegt in einem Raster mit
  // overflow-clip, und Safari beschneidet ueberstehende Panels dort auch
  // senkrecht (docs/tables.md, Fallstrick 7).
  //
  // Speichern passiert sofort je Auswahl -- es gibt nichts zu
  // bestaetigen, man SIEHT das Ergebnis hinter dem Dialog.

  import Icon from "./Icon.svelte";
  import { portal } from "./portal";
  import { t } from "./i18n.svelte";
  import type { CardConfig, Kartenmodus, ZahlAbschnitt } from "./card-config";

  interface Feld {
    slug: string;
    name: string;
    unit: string;
  }

  interface Props {
    titel: string;
    felder: Feld[];
    config: CardConfig;
    /** true: die Karte kann einen Verlauf zeichnen (Zahlen-Werte). */
    verlaufMoeglich?: boolean;
    /** Darstellungs-Modi (Messwert/Liste/Verlauf) -- die festen Karten
     *  des Dashboards haben keine, die zeigen immer dasselbe Geruest. */
    zeigeDarstellung?: boolean;
    /** Leitwert-Auswahl (die grosse Zahl). */
    zeigeLeit?: boolean;
    /** Ueberschrift ueber der Feld-Liste (Vorgabe: "Werte darunter"). */
    felderTitel?: string;
    zahlen?: ZahlAbschnitt[];
    onChange: (c: CardConfig) => void;
    onReset: () => void;
    onClose: () => void;
  }

  let {
    titel,
    felder,
    config,
    verlaufMoeglich = true,
    zeigeDarstellung = true,
    zeigeLeit = true,
    felderTitel,
    zahlen = [],
    onChange,
    onReset,
    onClose,
  }: Props = $props();

  const MODI: { id: Kartenmodus; label: string; hinweis: string }[] = [
    { id: "wert", label: t("cardconfig.mode.value", "Nur Messwert"), hinweis: t("cardconfig.mode.value_hint", "eine Zahl, sonst nichts") },
    { id: "liste", label: t("cardconfig.mode.list", "Werte-Liste"), hinweis: t("cardconfig.mode.list_hint", "Messwert plus die gewählten Felder") },
    { id: "verlauf", label: t("cardconfig.mode.graph", "Verlauf"), hinweis: t("cardconfig.mode.graph_hint", "Messwert plus Kurve der letzten Stunden") },
  ];
  const STUNDEN = [6, 24, 72];

  const sichtbareFelder = $derived(config.felder ?? felder.map((f) => f.slug));

  function setzeModus(m: Kartenmodus): void {
    onChange({ ...config, modus: m });
  }
  function setzeLeit(slug: string): void {
    onChange({ ...config, leit: slug });
  }
  function toggleFeld(slug: string): void {
    const drin = sichtbareFelder.includes(slug);
    onChange({
      ...config,
      felder: drin ? sichtbareFelder.filter((s) => s !== slug) : [...sichtbareFelder, slug],
    });
  }
</script>

<!-- Escape am Fenster, nicht am Overlay: der Fokus liegt nach dem Oeffnen
     noch auf dem Zahnrad in der Karte, dort kam das keydown nie an. -->
<svelte:window onkeydown={(e) => e.key === "Escape" && onClose()} />

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  use:portal
  class="bg-bg/70 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
  role="dialog"
  aria-modal="true"
  aria-label={t("cardconfig.title", "Karte einrichten")}
  tabindex="-1"
  onclick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  onkeydown={(e) => e.key === "Escape" && onClose()}
>
  <div class="surface border-border max-h-[85vh] w-full max-w-md overflow-auto rounded-xl border shadow-xl">
    <header class="border-border flex items-center justify-between border-b px-4 py-3">
      <div class="min-w-0">
        <div class="text-fg-subtle t-eyebrow">{t("cardconfig.title", "Karte einrichten")}</div>
        <div class="text-fg truncate text-sm font-medium">{titel}</div>
      </div>
      <button type="button" class="text-fg-muted hover:text-fg" onclick={onClose} aria-label={t("common.close", "Schließen")}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </header>

    <div class="grid gap-4 px-4 py-4">
      {#if zeigeDarstellung}
      <section class="grid gap-1.5">
        <h3 class="text-fg-subtle t-eyebrow">{t("cardconfig.display", "Darstellung")}</h3>
        <div class="grid gap-1.5">
          {#each MODI as m (m.id)}
            {#if m.id !== "verlauf" || verlaufMoeglich}
              <button
                type="button"
                onclick={() => setzeModus(m.id)}
                aria-pressed={config.modus === m.id}
                class="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition {config.modus === m.id
                  ? 'border-accent/50 bg-accent/10'
                  : 'border-border hover:bg-bg-muted/40'}"
              >
                <span class="min-w-0">
                  <span class="text-fg block text-sm font-medium">{m.label}</span>
                  <span class="text-fg-muted block t-meta">{m.hinweis}</span>
                </span>
                {#if config.modus === m.id}<Icon name="check" size={15} />{/if}
              </button>
            {/if}
          {/each}
        </div>
      </section>
      {/if}

      {#if zeigeDarstellung && config.modus === "verlauf"}
        <section class="grid gap-1.5">
          <h3 class="text-fg-subtle t-eyebrow">{t("cardconfig.window", "Zeitfenster")}</h3>
          <div class="flex flex-wrap gap-1.5">
            {#each STUNDEN as h (h)}
              <button
                type="button"
                onclick={() => onChange({ ...config, stunden: h })}
                aria-pressed={(config.stunden ?? 24) === h}
                class="rounded-lg border ctrl-s t-meta font-medium transition {(config.stunden ?? 24) === h
                  ? 'border-accent/50 bg-accent/10 text-accent'
                  : 'border-border text-fg-muted hover:text-fg'}"
              >
                {h} h
              </button>
            {/each}
          </div>
        </section>
      {/if}

      <!-- Zahlen-Abschnitte der festen Karten: Zeitfenster einer Kurve,
           Laenge einer Liste. Jede Karte bringt nur mit, was sie kann. -->
      {#each zahlen as z (z.feld + z.titel)}
        {@const aktuell = (z.feld === "stunden" ? config.stunden : config.anzahl) ?? z.vorgabe}
        <section class="grid gap-1.5">
          <h3 class="text-fg-subtle t-eyebrow">{z.titel}</h3>
          <div class="flex flex-wrap gap-1.5">
            {#each z.optionen as o (o.wert)}
              <button
                type="button"
                onclick={() => onChange({ ...config, [z.feld]: o.wert })}
                aria-pressed={aktuell === o.wert}
                class="rounded-lg border ctrl-s t-meta font-medium transition {aktuell === o.wert
                  ? 'border-accent/50 bg-accent/10 text-accent'
                  : 'border-border text-fg-muted hover:text-fg'}"
              >
                {o.label}
              </button>
            {/each}
          </div>
        </section>
      {/each}

      {#if zeigeLeit}
      <section class="grid gap-1.5">
        <h3 class="text-fg-subtle t-eyebrow">{t("cardconfig.lead", "Messwert (große Zahl)")}</h3>
        <div class="flex flex-wrap gap-1.5">
          {#each felder as f (f.slug)}
            <button
              type="button"
              onclick={() => setzeLeit(f.slug)}
              aria-pressed={config.leit === f.slug}
              class="rounded-lg border ctrl-s t-meta font-medium transition {config.leit === f.slug
                ? 'border-accent/50 bg-accent/10 text-accent'
                : 'border-border text-fg-muted hover:text-fg'}"
            >
              {f.name}{f.unit ? ` (${f.unit})` : ""}
            </button>
          {/each}
        </div>
      </section>
      {/if}

      {#if !zeigeDarstellung || config.modus !== "wert"}
        <section class="grid gap-1.5">
          <h3 class="text-fg-subtle t-eyebrow">{felderTitel ?? t("cardconfig.fields", "Werte darunter")}</h3>
          <div class="grid gap-1">
            {#each felder as f (f.slug)}
              <label class="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  id="cardcfg-{f.slug}"
                  checked={sichtbareFelder.includes(f.slug)}
                  onchange={() => toggleFeld(f.slug)}
                  class="accent-accent"
                />
                <span class="text-fg-muted truncate">{f.name}</span>
                <span class="text-fg-muted ml-auto t-meta">{f.unit}</span>
              </label>
            {/each}
          </div>
        </section>
      {/if}
    </div>

    <footer class="border-border flex items-center justify-between border-t px-4 py-3">
      <button type="button" class="text-fg-muted hover:text-fg t-meta underline decoration-dotted" onclick={onReset}>
        {t("cardconfig.reset", "Auf Vorgabe zurücksetzen")}
      </button>
      <button
        type="button"
        class="border-border text-fg-muted hover:bg-bg-muted rounded-md border ctrl-m text-sm font-medium transition"
        onclick={onClose}
      >
        {t("common.done", "Fertig")}
      </button>
    </footer>
  </div>
</div>
